/**
 * Générateur — Automatisme : connaître et utiliser les tables de
 * multiplication.
 * Attendu ID : 1047 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME n°9 (programme 2025), « entretenir la connaissance
 * et l'utilisation des tables ». Borne fixée au cycle 2 et jamais relevée :
 * A × B = C avec A et B entiers de 0 à 10 ; en pratique 2 à 9 (0, 1 et 10
 * gaspillent des items). Rien de nouveau à mémoriser : produit et facteur
 * manquant, dans les trois positions du cours moyen.
 * Labels (cartes exerciseur) — deux cartes (cadrage prof 2026-09-07) :
 *   - Série flash          (6 trous saisie, deux items par forme)
 *   - Dans les deux sens   (QCM, Vrai/Faux, Relier sur un fait de table)
 *
 * ── Plan de cartes validé (2026-09-07) ───────────────────────────────────────
 * TROIS FORMES, toutes multiplicatives (le quotient « 56 ÷ 7 » est le lien
 * multiplication/division, automatisme de 4e, exclu) :
 *   - produit         : 7 × 8 = …
 *   - facteur manquant : 6 × … = 54 ou … × 7 = 49 (trou devant ou derrière)
 *   - produit à gauche : 56 = 7 × … ou 56 = … × 8
 * La fluence se mesure au niveau de l'évaluation (durée réglée par le prof,
 * minuteur du quiz) : aucun chrono par item n'existe, la série de six est la
 * bonne longueur pour la laisser voir.
 *
 * DISTRACTEURS (QCM, V/F, leurre du Relier), trois familles nommées d'après
 * le corrigé de la fiche (« deux résultats voisins de la même table
 * confondus, 48 au lieu de 56 ») :
 *   - voisin_meme_table : a × (b ± 1)          7 × 8 → 49 ou 63
 *   - voisin_autre_table : (a ± 1) × b         7 × 8 → 48 ou 64
 *   - couple_voisin : (a ∓ 1) × (b ± 1)        7 × 8 → 54 (c'est 6 × 9)
 *     LA confusion la plus fréquente en 5e : un résultat de table plausible
 *     attaché au mauvais couple ; tirée une fois sur trois à la place d'une
 *     des deux autres.
 *   Carrés : les deux voisins coïncident (7 × 8 = 8 × 7) → on prend les deux
 *   voisins de la même table (42 et 56). Somme a + b exclue (trop faible).
 *   Toute collision de valeurs → nouveau tirage complet, jamais d'option
 *   inventée à l'assemblage.
 *
 * EXCLUSIONS garanties par assert : facteurs hors 2..9 (donc ni 0, 1, 10,
 * 11, 12), décimaux, dizaines, factorisation, fractions, mini-problèmes.
 * COUP DE POUCE (revue prof 2026-09-07) : le voisin de table, par l'aire
 * (7 × 8 = 7 × 7 + 7), un geste et non un fait. COUPLE SACRIFIÉ : 7 × 8 ne
 * sort d'AUCUNE carte, dans aucun ordre (63 couples restent) ; invariant
 * protégé par cdp1047.self.test.js.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, tex,
} from '../../../utils';

const ATTENDU_ID = 1047;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_SENS = 'Dans les deux sens';

const FOIS = '×';
const MIN_F = 2;
const MAX_F = 9;

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Le couple de la fiche d'aide, sacrifié dans les deux ordres. */
export const COUPLE_SACRIFIE = { a: 7, b: 8 };
const estSacrifie = (a, b) => (a === COUPLE_SACRIFIE.a && b === COUPLE_SACRIFIE.b) || (a === COUPLE_SACRIFIE.b && b === COUPLE_SACRIFIE.a);

/** Un fait de table a × b = p, facteurs dans 2..9, jamais le couple sacrifié (assert). */
const verifierFait = (a, b) => {
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < MIN_F || a > MAX_F || b < MIN_F || b > MAX_F) {
        throw new Error(`gen_1047 : facteur hors table ${a} x ${b}`);
    }
    if (estSacrifie(a, b)) throw new Error('gen_1047 : couple sacrifié tiré');
    return { a, b, p: a * b };
};

const tirerFait = () => {
    for (let essai = 0; essai < 60; essai++) {
        const a = randInt(MIN_F, MAX_F);
        const b = randInt(MIN_F, MAX_F);
        if (!estSacrifie(a, b)) return verifierFait(a, b);
    }
    return verifierFait(6, 9);
};
const tirerCarre = () => { const a = randInt(MIN_F, MAX_F); return verifierFait(a, a); };

/** Clé d'un couple non ordonné : 7×8 et 8×7 sont le même fait. */
const cleCouple = (f) => `${Math.min(f.a, f.b)}x${Math.max(f.a, f.b)}`;

const FORMES = ['produit', 'facteur', 'gauche'];

// ─── Écritures ────────────────────────────────────────────────────────────

/**
 * Une ligne KaTeX avec le trou à sa place, selon la forme et la position du
 * trou (facteur manquant et produit à gauche : le trou vise a ou b).
 */
const ligneFlash = (f, forme, trouSurA) => {
    const trou = (n) => '{{' + n + '}}';
    let corps;
    if (forme === 'produit') corps = `${f.a} \\times ${f.b} = ${trou(f.p)}`;
    else if (forme === 'facteur') corps = trouSurA ? `${trou(f.a)} \\times ${f.b} = ${f.p}` : `${f.a} \\times ${trou(f.b)} = ${f.p}`;
    else corps = trouSurA ? `${f.p} = ${trou(f.a)} \\times ${f.b}` : `${f.p} = ${f.a} \\times ${trou(f.b)}`;
    return '$' + corps + '$';
};

/** Égalité en texte brut (V/F, relier) : « 7 × 8 = 56 » ou « 56 = 7 × 8 ». */
const egaliteTexte = (a, b, p, gauche) => (gauche ? `${p} = ${a} ${FOIS} ${b}` : `${a} ${FOIS} ${b} = ${p}`);

// ─── Distracteurs (résultats voisins) ─────────────────────────────────────

const dansTable = (n) => n >= MIN_F && n <= MAX_F;

/**
 * Les deux distracteurs d'un fait, en PRODUITS : familles voisin_meme_table
 * et voisin_autre_table (carré : les deux voisins de la même table), l'une
 * remplacée une fois sur trois par couple_voisin. null si collision (le
 * tireur recommence un tirage complet).
 */
const distracteursProduit = (f) => {
    const candidats = [];
    const signe = () => randomChoice([-1, 1]);
    if (f.a === f.b) {
        candidats.push({ valeur: f.a * (f.a - 1), code: 'voisin_meme_table' });
        candidats.push({ valeur: f.a * (f.a + 1), code: 'voisin_meme_table' });
    } else {
        let s = signe();
        if (!dansTable(f.b + s)) s = -s;
        candidats.push({ valeur: f.a * (f.b + s), code: 'voisin_meme_table' });
        s = signe();
        if (!dansTable(f.a + s)) s = -s;
        candidats.push({ valeur: (f.a + s) * f.b, code: 'voisin_autre_table' });
    }
    if (randInt(1, 3) === 1) {
        // (a − 1)(b + 1) ou (a + 1)(b − 1), dans la table.
        const s = signe();
        const a2 = f.a - s;
        const b2 = f.b + s;
        if (dansTable(a2) && dansTable(b2)) {
            candidats[randInt(0, 1)] = { valeur: a2 * b2, code: 'couple_voisin' };
        }
    }
    const valeurs = new Set([f.p, ...candidats.map((c) => c.valeur)]);
    // Trois valeurs distinctes, toutes des résultats de table (4..81) : un
    // carré de 2 ou de 9 a un voisin hors table → tirage rejeté.
    if (valeurs.size !== 3 || candidats.some((c) => c.valeur < MIN_F * MIN_F || c.valeur > MAX_F * MAX_F)) return null;
    return candidats;
};

/**
 * Les deux distracteurs d'un facteur manquant : b ± 1 (voisin), l'un
 * remplacé une fois sur trois par un saut de deux (b ± 2). Au bord de la
 * table (b = 2 ou 9), le voisin manquant est remplacé par le saut de deux.
 */
const distracteursFacteur = (b) => {
    const candidats = [b - 1, b + 1].filter(dansTable).map((v) => ({ valeur: v, code: 'facteur_voisin' }));
    const sauts = [b - 2, b + 2].filter(dansTable);
    if (candidats.length < 2 && sauts.length) candidats.push({ valeur: sauts[0], code: 'saut_de_deux' });
    else if (randInt(1, 3) === 1 && sauts.length) {
        candidats[randInt(0, 1)] = { valeur: randomChoice(sauts), code: 'saut_de_deux' };
    }
    const valeurs = new Set([b, ...candidats.map((c) => c.valeur)]);
    return valeurs.size === 3 ? candidats : null;
};

const EXPLICATIONS = {
    voisin_meme_table: 'C\'est le résultat d\'à côté dans la même table : une fois le nombre en plus ou en moins.',
    voisin_autre_table: 'C\'est le résultat de la table voisine.',
    couple_voisin: 'Ce résultat existe dans les tables, mais il appartient à un autre couple.',
    facteur_voisin: 'Le facteur d\'à côté donne le résultat d\'à côté, pas celui-là.',
    saut_de_deux: 'Deux crans plus loin dans la table.',
    regle: 'Une table se récite dans les deux sens : du couple au résultat, et du résultat au facteur manquant.',
};

// ─── Carte 1 : Série flash (6 trous saisie) ───────────────────────────────

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        // Six faits distincts (couples non ordonnés), six résultats distincts,
        // un carré une fois sur deux. Deux items par forme, trou devant ou
        // derrière en alternance sur les formes à facteur.
        const faits = [];
        if (randomChoice([true, false])) faits.push(tirerCarre());
        while (faits.length < 6) faits.push(tirerFait());
        if (new Set(faits.map(cleCouple)).size < 6 || new Set(faits.map((f) => f.p)).size < 6) continue;
        const formes = shuffle(['produit', 'produit', 'facteur', 'facteur', 'gauche', 'gauche']);
        let bascule = randomChoice([true, false]);
        const items = faits.map((f, i) => {
            const trouSurA = formes[i] === 'produit' ? false : (bascule = !bascule);
            return { ...f, forme: formes[i], trouSurA };
        });
        const phrase = items.map((it) => ligneFlash(it, it.forme, it.trouSurA)).join('\n');
        const payload = makeTexteTrou(
            phrase,
            '<p><b>Complète chaque égalité.</b></p>',
            { ...opts(L_FLASH), mode: 'saisie' },
        );
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
        payload._meta.params = { items };
        return payload;
    }
    const items = [
        { ...verifierFait(6, 8), forme: 'produit', trouSurA: false },
        { ...verifierFait(6, 9), forme: 'produit', trouSurA: false },
        { ...verifierFait(8, 8), forme: 'facteur', trouSurA: true },
        { ...verifierFait(4, 7), forme: 'facteur', trouSurA: false },
        { ...verifierFait(9, 3), forme: 'gauche', trouSurA: true },
        { ...verifierFait(5, 6), forme: 'gauche', trouSurA: false },
    ];
    const phrase = items.map((it) => ligneFlash(it, it.forme, it.trouSurA)).join('\n');
    const payload = makeTexteTrou(phrase, '<p><b>Complète chaque égalité.</b></p>', { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2, format A : QCM (produit ou facteur manquant) ────────────────

const genQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const forme = randomChoice(FORMES);
        const f = randInt(1, 4) === 1 ? tirerCarre() : tirerFait();
        let bon;
        let faux;
        let texte;
        if (forme === 'produit') {
            faux = distracteursProduit(f);
            if (!faux) continue;
            bon = String(f.p);
            texte = `<p><b>Combien vaut ${tex(`${f.a} \\times ${f.b}`)} ?</b></p>`;
        } else {
            // Facteur manquant : le trou vise b (le couple est tiré au hasard,
            // donc la position du facteur cherché varie d'elle-même).
            faux = distracteursFacteur(f.b);
            if (!faux) continue;
            bon = String(f.b);
            const egalite = forme === 'facteur' ? `${f.a} \\times \\ldots = ${f.p}` : `${f.p} = ${f.a} \\times \\ldots`;
            texte = `<p><b>Quel nombre manque dans ${tex(egalite)} ?</b></p>`;
        }
        const payload = makeQcm([bon, ...faux.map((x) => String(x.valeur))], bon, texte, opts(L_SENS), false);
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...f, forme, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const f = verifierFait(6, 9);
    const payload = makeQcm(['54', '48', '63'], '54', `<p><b>Combien vaut ${tex('6 \\times 9')} ?</b></p>`, opts(L_SENS), false);
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...f, forme: 'produit', distracteurs: [{ code: 'voisin_meme_table' }, { code: 'voisin_autre_table' }] };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 égalités, 1 ou 2 vraies) ─────────

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const faits = [tirerFait(), tirerFait(), tirerFait()];
        if (new Set(faits.map(cleCouple)).size < 3) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const codes = [];
        let ok = true;
        const lignes = faits.map((f, i) => {
            const gauche = randomChoice([true, false]);
            if (verites[i]) return egaliteTexte(f.a, f.b, f.p, gauche);
            const faux = distracteursProduit(f);
            if (!faux) { ok = false; return ''; }
            const d = randomChoice(faux);
            codes.push(d.code);
            return egaliteTexte(f.a, f.b, d.valeur, gauche);
        });
        if (!ok || new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_SENS));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { faits, verites, distracteurs: codes.map((code) => ({ code })) };
        return payload;
    }
    const faits = [verifierFait(6, 8), verifierFait(6, 9), verifierFait(4, 7)];
    const lignes = ['6 × 8 = 48', '54 = 6 × 8', '4 × 7 = 28'];
    const payload = makeVraiFaux(lignes, [0, 1, 0], '<p><b>Vrai ou faux ?</b></p>', opts(L_SENS));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { faits, verites: [true, false, true], distracteurs: [{ code: 'voisin_meme_table' }] };
    return payload;
};

// ─── Carte 2, format C : Relier (4 produits, 4 résultats, 1 leurre) ───────

const genRelier = () => {
    for (let essai = 0; essai < 80; essai++) {
        const faits = [tirerFait(), tirerFait(), tirerFait(), tirerFait()];
        if (new Set(faits.map(cleCouple)).size < 4 || new Set(faits.map((f) => f.p)).size < 4) continue;
        // Le leurre : un voisin d'un des quatre faits, distinct des 4 résultats.
        const cible = randomChoice(faits);
        const voisins = distracteursProduit(cible);
        if (!voisins) continue;
        const leurre = randomChoice(voisins);
        if (faits.some((f) => f.p === leurre.valeur)) continue;
        const pairs = faits.map((f) => ({ source: `${f.a} ${FOIS} ${f.b}`, target: String(f.p) }));
        const payload = makeRelier(
            pairs,
            '<p><b>Relie chaque produit à son résultat.</b> Un résultat est en trop.</p>',
            { ...opts(L_SENS), cibles_leurres: [{ target: String(leurre.valeur), code: leurre.code }] },
        );
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { faits, leurre };
        return payload;
    }
    const faits = [verifierFait(6, 8), verifierFait(6, 9), verifierFait(8, 8), verifierFait(4, 7)];
    const payload = makeRelier(
        faits.map((f) => ({ source: `${f.a} ${FOIS} ${f.b}`, target: String(f.p) })),
        '<p><b>Relie chaque produit à son résultat.</b> Un résultat est en trop.</p>',
        { ...opts(L_SENS), cibles_leurres: [{ target: '42', code: 'voisin_meme_table' }] },
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { faits, leurre: { valeur: 42, code: 'voisin_meme_table' } };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'trois choix (surcharge à quatre : arbitrage prof 2026-09-07), tous des résultats ou des facteurs DE TABLE, dans 2..9 ou 4..81',
        'collision de valeurs → nouveau tirage complet, jamais d\'option inventée à l\'assemblage',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    qcm_unique: {
        support: 'un fait de table a × b, carré une fois sur quatre ; forme produit, facteur manquant ou produit à gauche',
        fixes: {
            voisin_meme_table: 'a × (b ± 1) : le résultat d\'à côté dans la même table (49 ou 63 pour 7 × 8)',
            voisin_autre_table: '(a ± 1) × b : le résultat de la table voisine (48 ou 64 pour 7 × 8)',
            facteur_voisin: 'b ± 1 quand on cherche un facteur',
        },
        rotation: {
            couple_voisin: '(a ∓ 1) × (b ± 1) : un résultat de table plausible attaché au mauvais couple (54 pour 7 × 8), une fois sur trois à la place d\'un voisin',
            saut_de_deux: 'b ± 2 pour un facteur, une fois sur trois',
        },
        carres: 'les deux voisins coïncident (7 × 8 = 8 × 7) → les deux voisins de la même table (42 et 56)',
    },
    vf_tableau_vf: 'mêmes familles, une par ligne fausse ; 1 ou 2 vraies sur 3',
    relier: 'une cible leurre = un voisin d\'un des quatre faits, distinct des quatre résultats',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    sens_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
    sens_relier: genRelier,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) pour le wrapper 4e gen_1093, qui reprend ces cartes ;
// sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'sens', label: L_SENS, etapes: 1, formatsList: [
        { key: 'sens_qcm_unique', label: 'QCM' },
        { key: 'sens_tableau_vf', label: 'Vrai/Faux' },
        { key: 'sens_relier', label: 'Relier' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
